/**
 * Input component with label and variants
 * Follows shadcn/ui patterns and AdVerdict design tokens
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { Search } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

const inputVariants = cva(
  'flex w-full font-sans text-md text-primary placeholder:text-muted transition-all duration-base ease-standard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'h-11 rounded-md border border-border-subtle bg-surface px-4 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12',
        search:
          'h-11 rounded-pill border border-border-subtle bg-surface pl-10 pr-5 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12',
      },
      error: {
        true: 'border-danger focus-visible:border-danger focus-visible:ring-danger/12',
      },
    },
    defaultVariants: {
      variant: 'default',
      error: false,
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      type = 'text',
      label,
      error = false,
      errorMessage,
      helperText,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || `input-${generatedId}`;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="mb-2 block text-sm font-medium text-secondary"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {variant === 'search' && (
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          )}
          <input
            type={type}
            className={cn(inputVariants({ variant, error, className }))}
            ref={ref}
            id={inputId}
            aria-invalid={error}
            aria-describedby={
              error && errorMessage
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            {...props}
          />
        </div>
        {error && errorMessage && (
          <p
            id={`${inputId}-error`}
            className="mt-2 text-xs text-danger"
            role="alert"
          >
            {errorMessage}
          </p>
        )}
        {!error && helperText && (
          <p id={`${inputId}-helper`} className="mt-2 text-xs text-muted">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
